import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, UserCircle2, Telescope, BellRing, Moon, CalendarDays } from 'lucide-react';
import StellarLogo from '@/ui/assets/brand/StellarSkyImager.webp';
import { LanguageSelector } from './LanguageSelector';
import { useNightVision } from './NightVisionContext';
import { useAuth } from '@/hooks/useAuth';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { Capacitor } from '@capacitor/core';

export const Header: React.FC<{ hidden?: boolean }> = ({ hidden }) => {
  if (hidden) return null;
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [plannerVersion, setPlannerVersion] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const location = useLocation();
  const { isNightVision, toggleNightVision } = useNightVision();
  const { user } = useAuth();
  const { sentinelPublic } = useSiteSettings();

  // Le menu déroulant s'ouvre au survol pour la souris, mais sur écran tactile (tablette/mobile
  // en vue desktop) il n'y a pas de vrai survol : Chrome émule un mouseenter au tap puis déclenche
  // le click, ce qui ouvrait puis refermait aussitôt le menu. On n'active donc le survol que pour
  // les pointeurs fins (souris) et on gère l'ouverture au tap via onClick + fermeture au tap dehors.
  const canHover = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;

  // Fetch planner version on mount
  useEffect(() => {
    fetch('/api/planner-version')
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setPlannerVersion(data.version))
      .catch(() => { /* silent fail */ });
  }, []);

  const navLinks = [
    { to: '/', label: t('header.nav.home') },
    { to: '/actualites', label: t('header.nav.news') },
    {
      id: 'tools',
      label: t('header.nav.tools'),
      children: [
        { to: '/planner', label: t('header.nav.planner') },
        { to: '/horizon-generator', label: t('header.nav.horizonGenerator') },
        // Lien Sentinel contextuel : connecté → « Mes projets » (hub de l'outil, toujours peuplé,
        // contrairement à « Ce soir » qui est vide avant le 1er tick), sinon → la landing
        // publique (conversion + SEO). Cohérent avec Planner/Horizon qui pointent vers l'outil.
        // [Interrupteur Sentinel] Fermé + anonyme → entrée masquée (elle mènerait à la landing/promo).
        ...((user || sentinelPublic) ? [{ to: user ? '/sentinel/projects' : '/sentinel', label: t('header.nav.sentinel') }] : []),
      ]
    },
    {
      id: 'resources',
      label: t('header.nav.resources'),
      children: [
        { to: '/videos', label: t('header.nav.videos') },
        { to: '/guides', label: t('header.nav.guides') },
        { to: '/ressources', label: t('header.nav.files') },
      ]
    },
    {
      id: 'whoAmI',
      label: t('header.nav.whoAmI'),
      children: [
        { to: '/a-propos', label: t('header.nav.about') },
        { to: '/materiel', label: t('header.nav.equipment') },
        { to: '/galerie', label: t('header.nav.gallery') },
      ]
    },
    { to: '/contact', label: t('header.nav.contact') },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0d1117]/90 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src={StellarLogo} alt="StellarSkyImager" className="h-10 w-auto" width={40} height={40} />
            <span className="text-xl font-bold tracking-tight text-white hidden sm:block">StellarSkyImager</span>
          </Link>

          <nav className="hidden md:flex gap-6 lg:gap-8 items-center">
            {navLinks.map(link => {
              if (link.children) {
                const isChildActive = link.children.some(child => location.pathname === child.to);
                const isOpen = openDropdown === link.id;
                const isTools = link.id === 'tools';

                return (
                  <div
                    key={link.id}
                    className="relative group"
                    onMouseEnter={canHover ? () => setOpenDropdown(link.id!) : undefined}
                    onMouseLeave={canHover ? () => setOpenDropdown(null) : undefined}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenDropdown(prev => (prev === link.id ? null : link.id!))}
                      aria-haspopup="menu"
                      aria-expanded={isOpen}
                      className={isTools
                        ? `font-semibold flex items-center gap-1.5 text-sm px-2.5 py-1 bg-primary/[0.12] border border-primary/25 rounded-lg transition-all ${isChildActive ? 'text-primary bg-primary/20' : 'text-primary hover:bg-primary/20'}`
                        : `font-medium transition-all flex items-center gap-1 text-sm py-2 ${isChildActive ? 'text-primary' : 'text-white/70 hover:text-white'}`
                      }
                    >
                      {isTools && (
                        <span className="material-symbols-outlined text-sm">auto_awesome</span>
                      )}
                      {link.label}
                      <span className={`material-symbols-outlined text-sm transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                        expand_more
                      </span>
                    </button>

                    {/* Tactile : couche de fermeture au tap dehors (le survol ne ferme pas sur touch) */}
                    {isOpen && !canHover && (
                      <div className="fixed inset-0 z-40" onClick={() => setOpenDropdown(null)} />
                    )}

                    {/* Dropdown Menu */}
                    <div className={`absolute top-full left-1/2 -translate-x-1/2 w-52 pt-2 z-50 transition-all duration-200 ${isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
                      <div className="bg-[#0d1117]/95 backdrop-blur-xl border border-white/10 rounded-xl p-2 shadow-2xl shadow-black/50">
                        {link.children.map(child => (
                          <Link
                            key={child.to}
                            to={child.to}
                            onClick={() => setOpenDropdown(null)}
                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${location.pathname === child.to
                              ? 'bg-primary/10 text-primary'
                              : 'text-white/70 hover:bg-white/5 hover:text-white'
                              }`}
                          >
                            <span className="flex-1">{child.label}</span>
                            {child.to === '/planner' && plannerVersion && (
                              <span className="text-[10px] font-mono text-primary/50">v{plannerVersion}</span>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={link.to}
                  to={link.to!}
                  className={`font-medium transition-all flex items-center gap-1 text-sm ${location.pathname === link.to
                    ? 'text-primary'
                    : 'text-white/70 hover:text-white'
                    }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-4">
            {/* Toggle Vision Nocturne */}
            <button
              onClick={toggleNightVision}
              className={`p-2 rounded-lg transition-all duration-300 border
                ${isNightVision
                  ? 'bg-red-900/30 border-red-500/40 text-red-400 shadow-[0_0_10px_rgba(220,38,38,0.3)] hover:bg-red-900/50'
                  : 'bg-transparent border-transparent text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
              title={isNightVision ? t('nightVision.enabled') : t('nightVision.disabled')}
            >
              <Eye size={18} strokeWidth={isNightVision ? 2.5 : 2} />
            </button>

            {/* Language Selector */}
            <LanguageSelector />

            {/* Compte utilisateur Sentinel (US 6.1/6.4) — connecté : menu (Mon compte / Mes projets) ; sinon : lien connexion */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setAccountMenuOpen(o => !o)}
                  title={t('auth.header.account')}
                  aria-haspopup="menu"
                  aria-expanded={accountMenuOpen}
                  className={`p-2 rounded-lg transition-all duration-300 border relative
                    ${location.pathname === '/account' || location.pathname.startsWith('/sentinel/')
                      ? 'bg-primary/10 border-primary/25 text-primary'
                      : 'bg-transparent border-transparent text-zinc-400 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <UserCircle2 size={18} />
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-400" />
                </button>
                {accountMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setAccountMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 z-50 w-52">
                      <div className="bg-[#0d1117]/95 backdrop-blur-xl border border-white/10 rounded-xl p-2 shadow-2xl shadow-black/50">
                        <Link
                          to="/account"
                          onClick={() => setAccountMenuOpen(false)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${location.pathname === '/account' ? 'bg-primary/10 text-primary' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                        >
                          <UserCircle2 size={16} />
                          <span className="flex-1">{t('auth.header.account')}</span>
                        </Link>
                        <Link
                          to="/sentinel/tonight"
                          onClick={() => setAccountMenuOpen(false)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${location.pathname === '/sentinel/tonight' ? 'bg-primary/10 text-primary' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                        >
                          <Moon size={16} />
                          <span className="flex-1">{t('sentinel.tonight.pageTitle')}</span>
                        </Link>
                        <Link
                          to="/sentinel/calendar"
                          onClick={() => setAccountMenuOpen(false)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${location.pathname === '/sentinel/calendar' ? 'bg-primary/10 text-primary' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                        >
                          <CalendarDays size={16} />
                          <span className="flex-1">{t('sentinel.calendar.menuTitle')}</span>
                        </Link>
                        <Link
                          to="/sentinel/projects"
                          onClick={() => setAccountMenuOpen(false)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${location.pathname === '/sentinel/projects' ? 'bg-primary/10 text-primary' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                        >
                          <Telescope size={16} />
                          <span className="flex-1">{t('sentinel.projects.title')}</span>
                        </Link>
                        <Link
                          to="/sentinel/alerts"
                          onClick={() => setAccountMenuOpen(false)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${location.pathname === '/sentinel/alerts' ? 'bg-primary/10 text-primary' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                        >
                          <BellRing size={16} />
                          <span className="flex-1">{t('sentinel.alerts.title')}</span>
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                title={t('auth.header.login')}
                className={`p-2 rounded-lg transition-all duration-300 border relative
                  ${location.pathname === '/login'
                    ? 'bg-primary/10 border-primary/25 text-primary'
                    : 'bg-transparent border-transparent text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
              >
                <UserCircle2 size={18} />
              </Link>
            )}

            {!Capacitor.isNativePlatform() && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden text-white p-2 hover:bg-white/5 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined text-2xl">
                  {isMobileMenuOpen ? 'close' : 'menu'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu (Web only) */}
      {!Capacitor.isNativePlatform() && isMobileMenuOpen && (
        <div className="md:hidden bg-[#0d1117] border-t border-white/10 max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="px-4 py-4 space-y-2">
            {/* Language Selector in mobile menu */}
            <div className="pb-3 mb-3 border-b border-white/10">
              <div className="flex justify-center">
                <LanguageSelector />
              </div>
            </div>
            {navLinks.map(link => {
              if (link.children) {
                return (
                  <div key={link.id} className="space-y-1">
                    <div className="text-xs font-bold text-white/40 uppercase tracking-widest px-3 py-2">
                      {link.label}
                    </div>
                    {link.children.map(child => (
                      <Link
                        key={child.to}
                        to={child.to}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all ${location.pathname === child.to
                          ? 'bg-primary/10 text-primary shadow-lg shadow-primary/5'
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                          }`}
                      >
                        <span className="flex-1">{child.label}</span>
                        {child.to === '/planner' && plannerVersion && (
                          <span className="text-[10px] font-mono text-primary/50">v{plannerVersion}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                );
              }

              return (
                <Link
                  key={link.to}
                  to={link.to!}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all ${location.pathname === link.to
                    ? 'bg-white/5 text-primary shadow-lg shadow-black/20'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                    }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
};
